import {
  Experiment,
  ExperimentConfig,
  type Exposure,
  type Variant,
  type Variants,
} from '@amplitude/experiment-js-client';
import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { Atom, atom, useAtomValue } from 'jotai';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { datadog } from '@flipster/utils/datadog';
import { EnvScope } from '@flipster/utils/env';
import { EventEmitter } from '@flipster/utils/event-emitter';
import { getLocalStorage, getPrexInstallationId, setLocalStorage } from '@flipster/utils/localStorage';
import segment, { SegmentTypes } from '@flipster/utils/segment';
import { segmentBunja } from '@flipster/utils/segment/bunja';

import { userIdBunja } from '../auth/state';

interface AbTestOptions {
  defaultValue?: string;
  /**
   * An atom that controls when to use the defaultValue instead of the actual variant.
   * While this atom holds `true`, the variant resolves to `defaultValue` (or `'off'`).
   * Useful for deferring the A/B test result until a prerequisite condition is met
   * (e.g., a loading state, a feature readiness flag).
   * Primarily used to prevent hydration mismatches by holding the default value
   * until client-side state is ready.
   */
  defaultWhileAtom?: Atom<boolean>;
}

export function useVariant(flagKey: string, options?: AbTestOptions): string | undefined {
  const { getVariantAtom } = useBunja(abTestBunja);
  const { variantAtom } = getVariantAtom(flagKey, options);
  return useAtomValue(variantAtom);
}

export function useAbFlag(flagKey: string, targetVariant: string, options?: AbTestOptions): boolean {
  const { getAbFlagAtom } = useBunja(abTestBunja);
  const abFlagAtom = getAbFlagAtom(flagKey, targetVariant, options);
  return useAtomValue(abFlagAtom);
}

interface AbTestEvents {
  exposure: Exposure;
}

type RawVariantAtom = Atom<Variant | undefined>;
interface VariantAtomFamily {
  rawVariantAtom: RawVariantAtom;
  variantAtom: Atom<string>;
}

export const abTestBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const env = bunja.use(EnvScope);
  const { userIdForTrackAtom } = bunja.use(userIdBunja);
  const { anonymousIdAtom: deviceIdAtom } = bunja.use(segmentBunja);

  const events = new EventEmitter<AbTestEvents>();
  const initialVariants = getInitialVariants();
  const hasInitialVariants = Boolean(initialVariants) && Object.keys(initialVariants).length > 0;
  const experimentConfig: ExperimentConfig = {
    serverUrl: 'https://api2.lab.amplitude.com',
    exposureTrackingProvider: {
      track: (exposure) => {
        events.emit('exposure', exposure);
        segment.track('$exposure' as SegmentTypes, exposure);
      },
    },
  };
  // 타입상 initialVariants 필드에 undefined를 넣을 수 있다고 나오나
  // 실제로 null이나 undefined를 대입해보면 런타임 오류가 발생함
  if (initialVariants) experimentConfig.initialVariants = initialVariants;
  const experiment = Experiment.initialize(env.AMPLITUDE_DEPLOYMENT_KEY, experimentConfig);

  function getUserId() {
    return store.get(userIdForTrackAtom);
  }

  function getDeviceId() {
    return store.get(deviceIdAtom);
  }

  function getInitialVariants() {
    const userIdOrDeviceId = getUserId() || getDeviceId();
    if (!userIdOrDeviceId) return {};
    return getLocalStorage(`flipster.configs.v1.amplitude.${userIdOrDeviceId}.variants`) as Variants;
  }

  const variantAtoms: Record<string, VariantAtomFamily> = {};
  function getVariantAtom(flagKey: string, options?: AbTestOptions): VariantAtomFamily {
    if (variantAtoms[flagKey]) return variantAtoms[flagKey];
    const rawVariantAtom = atom((get) => {
      const userIdOrDeviceId = getUserId() || getDeviceId();
      const isFetching = get(fetchingAtom);
      if (isFetching && !hasInitialVariants) return;
      if (!userIdOrDeviceId) return;
      return experiment.variant(flagKey);
    });
    const variantAtom = atom((get) => {
      const rawVariantValue = get(rawVariantAtom)?.value;
      const defaultWhile = options?.defaultWhileAtom ? get(options.defaultWhileAtom) : false;
      const defaultValue = options?.defaultValue || 'off';
      const variant = defaultWhile ? defaultValue : rawVariantValue || defaultValue;
      if (flagKey) datadog.setContext(`ab-test-flag.${flagKey}`, variant);
      return variant;
    });
    return (variantAtoms[flagKey] = { rawVariantAtom, variantAtom });
  }

  const flagAtoms: Record<string, Atom<boolean>> = {};
  function getAbFlagAtom(flagKey: string, targetVariant: string, options?: AbTestOptions): Atom<boolean> {
    if (flagAtoms[flagKey]) return flagAtoms[flagKey];
    return (flagAtoms[flagKey] = atom((get) => {
      const variant = get(getVariantAtom(flagKey, options).variantAtom);
      return variant === targetVariant;
    }));
  }

  function getVariant(flagKey: string, options?: AbTestOptions): string {
    return store.get(getVariantAtom(flagKey, options).variantAtom);
  }

  function getAbFlag(flagKey: string, targetVariant: string, options?: AbTestOptions): boolean {
    return store.get(getAbFlagAtom(flagKey, targetVariant, options));
  }

  const prexInstallationId = getPrexInstallationId();

  const fetchingAtom = atom(false);
  const fetchedAtom = atom(false);

  async function fetchExperiment() {
    const userIdOrDeviceId = getUserId() || getDeviceId();
    if (!userIdOrDeviceId) return experiment.clear();
    try {
      store.set(fetchingAtom, true);
      /**
       * amplitude에서 실험 생성 시 롤아웃 버킷팅 대상을 무조건 prex_installation_id 로 설정해야 한다.
       * https://prextech.slack.com/archives/C08LLK73DDE/p1754546594730269?thread_ts=1753328755.697299&cid=C08LLK73DDE
       */
      await experiment.fetch({
        user_id: getUserId(),
        device_id: getDeviceId(),
        user_properties: {
          prex_installation_id: prexInstallationId,
        },
      });
      store.set(fetchedAtom, true);
      setLocalStorage(`flipster.configs.v1.amplitude.${userIdOrDeviceId}.variants`, experiment.all());
    } finally {
      store.set(fetchingAtom, false);
    }
  }

  bunja.effect(() => {
    const unsubUserId = store.sub(userIdForTrackAtom, fetchExperiment);
    const unsubDeviceId = store.sub(deviceIdAtom, fetchExperiment);
    // 웹소켓 부분 구독 이후 다시 추가하도록 함
    // const unsubWsEvent = r230522Events.on('adhoc/public', (adhoc) => {
    //   if (adhoc === 'ReloadABTestUrgently') fetchExperiment();
    // });
    const oneMinute = 1000 * 60;
    const interval = setInterval(fetchExperiment, oneMinute * 10);
    fetchExperiment();
    return () => {
      unsubUserId();
      unsubDeviceId();
      // unsubWsEvent();
      clearInterval(interval);
      experiment.clear();
    };
  });

  return {
    events,
    experiment,
    getVariantAtom,
    getAbFlagAtom,
    getVariant,
    getAbFlag,
    userIdForTrackAtom,
    deviceIdAtom,
    prexInstallationId,
    fetchingAtom,
    fetchedAtom,
  };
});
abTestBunja.debugLabel = 'abTestBunja';
